import { NewsletterCampaignStats } from '@hooks-api';

type TagStatus = NewsletterCampaignStats['tagStatus'];
/** The statuses that mean Postmark cannot be trusted to answer for this campaign. */
type Unattributable = Exclude<TagStatus, 'ok'>;

/**
 * The header block a marketer pastes into Listmonk (campaign → Advanced → custom
 * headers) to make a campaign attributable. Identical text for every campaign —
 * Listmonk resolves the expressions per recipient on send, and a cloned campaign
 * inherits them. Mirrors `RECOMMENDED_CAMPAIGN_HEADERS` in the backend, and
 * `tagStatus.test.ts` pins it to the copy the API documents so the panel can
 * never tell marketers to paste something the backend then reads as wrong.
 */
export const CAMPAIGN_HEADER_BLOCK = `[
  {"X-PM-Tag": "{{ .Campaign.UUID }}"},
  {"X-PM-Metadata-campaign": "{{ .Campaign.Name }}"},
  {"X-PM-Metadata-subid": "{{ .Subscriber.UUID }}"}
]`;

/**
 * Whether Postmark can tie its engagement numbers to this campaign at all. A type
 * predicate, so the caller's `!attributable` branch narrows to the statuses the
 * copy below actually covers.
 */
export const isAttributable = (tagStatus: TagStatus): tagStatus is 'ok' => tagStatus === 'ok';

const REASON: Record<Unattributable, string> = {
  missing:
    'This campaign carries no X-PM-Tag header, so Postmark was never told which campaign its messages ' +
    'belong to and cannot report on them.',
  mismatch:
    "This campaign's X-PM-Tag does not resolve to its own uuid, so Postmark is being asked about a " +
    'different tag than the one its messages carry.',
};

/** Short label for the picker. Distinct per status: the two need opposite fixes. */
const CHIP: Record<Unattributable, string> = {
  missing: 'no tag',
  // Not "no tag": the header is there and wrong. Told to *add* the block, a
  // marketer would leave the bad value in place, and a second, differing
  // X-PM-Tag keeps the backend's `every()` check at mismatch permanently.
  mismatch: 'wrong tag',
};

/**
 * The types below say `Unattributable`, but a status the backend adds before this
 * client is regenerated still arrives here at runtime — narrowing only rules it
 * out on paper. So every lookup keeps a way to say something true rather than
 * render nothing.
 */
const asKnown = (tagStatus: Unattributable): Unattributable | null =>
  tagStatus === 'missing' || tagStatus === 'mismatch' ? tagStatus : null;

export const tagStatusReason = (tagStatus: Unattributable): string => {
  const known = asKnown(tagStatus);
  return known
    ? REASON[known]
    : 'Postmark cannot match this campaign by its X-PM-Tag header, so its engagement is not reported here.';
};

export const tagStatusChip = (tagStatus: Unattributable): string => {
  const known = asKnown(tagStatus);
  return known ? CHIP[known] : 'tag problem';
};

/** Said in both arms: the block is not per-campaign, and hardcoding a uuid breaks every clone. */
const SAME_FOR_EVERY_CAMPAIGN = 'The block is the same text for every campaign, and a clone inherits it.';

/**
 * How to apply the block. `missing` is the only status with nothing to displace —
 * the backend reports it exactly when there is no `X-PM-Tag` at all. Every other
 * unattributable status means a header is already there, so the safe default is
 * to **replace**: pasting the block alongside leaves two differing X-PM-Tag
 * values, which the backend's `every()` check still reads as broken. That is why
 * an unrecognised status gets the replace wording rather than the additive one —
 * additive is not the harmless choice here, it is the one that can entrench the
 * problem.
 *
 * "Every" is load-bearing: two X-PM-Tag entries is itself one of the ways to
 * reach `mismatch`, so replacing only "the" header can leave the stray one behind.
 */
export const tagStatusInstruction = (tagStatus: Unattributable): string =>
  asKnown(tagStatus) === 'missing'
    ? `Add this in Listmonk (campaign → Advanced → custom headers). ${SAME_FOR_EVERY_CAMPAIGN}`
    : `In Listmonk (campaign → Advanced → custom headers), remove every X-PM-Tag entry the campaign has and paste this block — leaving one behind keeps two conflicting tags, which is still wrong. ${SAME_FOR_EVERY_CAMPAIGN}`;

/**
 * What the numbers under the alert are worth. Headers are mutable while sent mail
 * is not, so engagement can legitimately arrive for a campaign whose header is
 * now wrong or gone — and the two failing statuses differ in what that means:
 * with `missing` the tag was this campaign's, with `mismatch` the messages
 * answering may belong to whatever else carries this uuid.
 */
export const tagStatusConsequence = (tagStatus: Unattributable, hasNumbers: boolean): string => {
  const known = asKnown(tagStatus);
  const sent = 'A campaign that has already been sent cannot be repaired this way';
  if (!hasNumbers) {
    if (known === 'missing') return `${sent} — its messages went out untagged.`;
    if (known === 'mismatch') return `${sent} — its messages went out under a different tag.`;
    // An unrecognised status must not borrow either story — including the
    // mismatch one: we cannot tell what tag, if any, its messages carried.
    return `${sent}, and we cannot tell what tag, if any, its messages carried.`;
  }
  return known === 'missing'
    ? 'The numbers below are real: those messages did carry this campaign’s tag, and the header was changed afterwards.'
    : 'The numbers below are whatever Postmark holds against this uuid, which may belong to another campaign.';
};
